import React, {useCallback, useEffect, useMemo, useState} from 'react';
import RenderIf from '@src/components/RenderIf/RenderIf';
import Translate from '@src/components/Translate/Translate';
import classnames from '@src/helpers/classnames';
import app from '@src/helpers/app';

const defaultProcessor = (value: any) => value;

export default function ListTruncatedValue(props: {
    truncatedString: string,
    truncateTo: number,
    controllerName: string,
    modelId: string | number,
    columnName: string,
    valueProcessor?: ((value: any) => any) | null,
    height?: number | null,
}) {
    const {truncatedString, truncateTo, controllerName, modelId, columnName} = props;
    const valueProcessor = props.valueProcessor ?? defaultProcessor;
    const maxHeight = props.height;

    const [isFetching, setIsFetching] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [expanded, setExpanded] = useState(false);
    const [value, setValue] = useState<string | null>(null);

    const processedValue = useMemo(
            () => valueProcessor(expanded ? (value ?? truncatedString) : truncatedString),
            [expanded, truncatedString, value, valueProcessor]
    );

    useEffect(() => {
        if (!errorMessage) {
            return;
        }

        const timer = setTimeout(() => setErrorMessage(null), 5000);

        return () => {
            clearTimeout(timer);
        };
    }, [errorMessage]);

    const onExpand = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();

        if (value) {
            setExpanded(was => !was);

            return;
        }

        setIsFetching(true);
        setErrorMessage(null);

        app('http').route('abstract-models.get-truncated-property', {
            id: String(modelId),
            property: columnName,
        }, {
            json: {'controller-name': controllerName}
        }).catch(e => {
            setErrorMessage(e?.message ?? 'Something went wrong');
        }).finally(() => {
            setIsFetching(false);
        }).then((response?: { value?: string }) => {
            if (response == null || !('value' in response)) {
                throw new Error('Unexpected response');
            }

            if (response.value != null) {
                setValue(response.value);
            }
            setExpanded(true);
        });
    }, [columnName, controllerName, modelId, value]);

    const isTruncated = truncatedString.length === truncateTo;
    if (!isTruncated) {
        return <>{processedValue}</>;
    }

    return <div
            className={classnames('models-list__truncated-value', {
                'models-list__truncated-value--expanded': expanded,
            })}
            style={expanded && maxHeight ? {maxHeight} : undefined}
    >
        <div className="models-list__truncated-value__content">{processedValue}</div>

        <div className="models-list__truncated-value__backdrop">
            <RenderIf expression={!errorMessage}>
                <button
                        type="button"
                        className={classnames('btn btn-sm', {
                            'btn-outline-info': !expanded,
                            'btn-outline-secondary': expanded,
                        })}
                        onClick={onExpand}
                >
                    <RenderIf expression={isFetching}>
                        <i className="fa fa-spin fa-spinner me-2"/>
                        <Translate message="@common-phrases.loading"/>
                    </RenderIf>

                    <RenderIf expression={!isFetching}>
                        <Translate message={`@common-phrases.${expanded ? 'collapse' : 'show-more'}`}/>
                    </RenderIf>
                </button>
            </RenderIf>

            <RenderIf expression={errorMessage}>
                <div className="alert alert-danger px-1 py-1 m-0">
                    <Translate message={errorMessage as string}/>
                </div>
            </RenderIf>
        </div>
    </div>;
}
